import { useRef } from 'react'

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedNumber, useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'

import {
  useIsPforStorefrontExcluded,
  useProblemFreeOrderRateAnalytics,
} from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useCardLocation } from '@/hooks/location/useCardLocation'

const ns = 'Listing.facilityStats'

type ScoreColor = 'blue' | 'green'

const SCORE_COLORS: Record<ScoreColor, string> = {
  blue: Colors.Semantic.BStock.onhover,
  green: '#2D8653',
}

// Value is a decimal between 0 and 1 (e.g. 0.95 = 95%)
const getScoreColor = (value: number): ScoreColor => {
  if (value >= 0.95) return 'green'
  return 'blue'
}

const StatRow = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
`

const LabelGroup = styled.div`
  ${Typography.Body4}
  align-items: center;
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  font-weight: 600;
  gap: 0.25rem;
`

const TooltipIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
  font-size: 0.75rem;
`

const ValueText = styled.span<{ $value: number | null }>`
  ${Typography.Subtitle1}
  color: ${({ $value }) =>
    $value === null
      ? SCORE_COLORS['blue']
      : SCORE_COLORS[getScoreColor($value)]};
`

const Description = styled.p`
  ${Typography.Caption}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

const ProgressTrack = styled.div`
  background: ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 99px;
  height: ${pxToRem(5)};
  margin-top: 0.25rem;
  overflow: hidden;
  width: 100%;
`

const ProgressFill = styled.div<{ $value: number }>`
  background: ${(o) => SCORE_COLORS[getScoreColor(o.$value)]};
  border-radius: 99px;
  height: 100%;
  width: ${(o) => `${o.$value * 100}%`};
`

const FooterText = styled.div`
  ${Typography.Subtitle2}
  align-items: center;
  color: ${Colors.Semantic.Neutral.dark_grey};
  display: flex;
  gap: 0.25rem;
  justify-content: flex-start;
`

export const FacilityStatsCard = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const tooltipIconRef = useRef<HTMLSpanElement>(null)
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const config = useFlag(FeatureFlags.ENABLE_LDP_PROBLEM_FREE_ORDER_RATE)
  const variant = config.variant || 'c'
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const personId = useDecodedAccessToken()?.personId
  const { facility_performance_rate, is_facility_performance_rate_shown } =
    useProblemFreeOrderRateAnalytics(listing)
  const isStorefrontExcluded = useIsPforStorefrontExcluded(listing)

  const handleTooltipShow = () => {
    trackButtonClicked({
      buttonName: 'facility_performance_rate_tooltip',
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      ...getListingAnalyticsData(listing, fmvBadgeFlag),
      facility_performance_rate,
      is_facility_performance_rate_shown,
    })
  }

  const value =
    (listing as { problemFreeOrderRate?: number | null })
      .problemFreeOrderRate ?? null
  const location = listing.lot.location
  const shortAddress = useCardLocation(location)
  const addressName =
    'addressName' in location ? location.addressName : undefined

  const footerText = [shortAddress, addressName].filter(Boolean).join(' | ')

  if (!config.enabled || !personId || isStorefrontExcluded) return null

  return (
    <Card data-testid="facility-stats-card">
      <CardHeader $variant="sidebar">
        <CardTitle $variant="sidebar">
          <FormattedMessage id={`${ns}.cardTitle`} />
        </CardTitle>
      </CardHeader>

      <CardBody $variant="sidebar">
        <StatRow>
          <LabelGroup>
            <FormattedMessage id={`Filters.problemFreeOrderRate.${variant}`} />
            <span
              ref={tooltipIconRef}
              role="img"
              tabIndex={0}
              aria-label={formatMessage({
                id: 'Filters.problemFreeOrderRate.tooltipAriaLabel',
              })}
            >
              <TooltipIcon icon={faCircleInfo} />
            </span>
            <Tooltip
              // oxlint-disable-next-line react/refs
              referenceElement={tooltipIconRef.current}
              onShow={handleTooltipShow}
            >
              <FormattedMessage id={`${ns}.problemFreeOrderRate.tooltip`} />
            </Tooltip>
          </LabelGroup>
          <ValueText $value={value} data-testid="facility-stats-card-value">
            {value !== null ? (
              <FormattedNumber
                value={value}
                style="percent"
                maximumFractionDigits={0}
              />
            ) : (
              <FormattedMessage
                id={`${ns}.problemFreeOrderRate.insufficientData`}
              />
            )}
          </ValueText>
        </StatRow>
        <Description>
          <FormattedMessage id={`${ns}.problemFreeOrderRate.description`} />
        </Description>
        <ProgressTrack>
          {value !== null && <ProgressFill $value={value} />}
        </ProgressTrack>
      </CardBody>

      <CardFooter>
        <FooterText>
          <FontAwesomeIcon icon={faLocationDot} size="xs" />
          <div>{footerText}</div>
        </FooterText>
      </CardFooter>
    </Card>
  )
}
